/** Sufijo de presentación: x250gr, x500 ml, x1000gr, etc. */
const PRESENTATION_SUFFIX_RE = /\s+(x?\d+(?:[.,]\d+)?\s*(?:gr|g|ml|l|kg|und|unidades?))\s*$/i;

export const extractPresentationLabel = (name) => {
  const match = (name || '').match(PRESENTATION_SUFFIX_RE);
  return match ? match[1].replace(/\s+/g, ' ').trim() : null;
};

export const extractBaseName = (name) => {
  let base = (name || '').replace(PRESENTATION_SUFFIX_RE, '').trim();
  // Claim E1: "Semilla de calabaza · energía suave" → título corto
  if (base.includes(' · ')) {
    base = base.split(' · ')[0].trim();
  } else if (base.includes(' - ')) {
    const parts = base.split(' - ');
    if (parts.length >= 2 && parts[0].length >= 3) base = parts[0].trim();
  }
  return base;
};

/** Claim / subtítulo tras el separador de marca (si existe). */
export const extractProductClaim = (name) => {
  const raw = (name || '').replace(PRESENTATION_SUFFIX_RE, '').trim();
  if (raw.includes(' · ')) {
    const parts = raw.split(' · ');
    return parts.slice(1).join(' · ').trim() || null;
  }
  return null;
};

/** Clave normalizada para agrupar familias (tolerante a singular/plural y tildes). */
export const normalizeProductFamilyKey = (name) => {
  const base = extractBaseName(name) || name || '';
  return base
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\bpistachos\b/g, 'pistacho')
    .replace(/\s+/g, ' ')
    .trim();
};

const parsePresentationValue = (label) => {
  if (!label) return 0;
  const match = label.match(/(\d+(?:[.,]\d+)?)/);
  return match ? parseFloat(match[1].replace(',', '.')) : 0;
};

export const detectPresentationUnitLabel = (labels) => {
  const joined = labels.join(' ').toLowerCase();
  if (/\bml\b/.test(joined) || /\b\d+\s*l\b/.test(joined)) return 'Mililitros';
  if (/\bgr\b/.test(joined) || /\bg\b/.test(joined) || /\bkg\b/.test(joined)) return 'Gramos';
  return 'Presentación';
};

const mapProductToPresentationVariant = (p) => {
  const label = extractPresentationLabel(p.name) || p.name;
  const isDropshipping = p.fulfillment === 'dropshipping';
  const price = parseFloat(p.price);
  const rawOriginal = parseFloat(
    p.originalPrice ?? p.original_price ?? p.compare_at_price ?? p.compareAtPrice ?? 0
  );
  const img = (p.images && p.images[0]) || p.image || '/images/placeholders/placeholder-product.png';

  return {
    id: p.id,
    name: p.name,
    label,
    price,
    originalPrice: Number.isFinite(rawOriginal) && rawOriginal > price ? rawOriginal : null,
    stock: parseInt(p.stock ?? 0, 10),
    inStock: parseInt(p.stock ?? 0, 10) > 0 || isDropshipping,
    isDropshipping,
    fulfillment: p.fulfillment || 'javiland',
    image: img,
    images: p.images || [img],
    description: p.description || '',
    brand: p.brand || '',
  };
};

/** Agrupa productos hermanos con el mismo nombre base y presentaciones distintas. */
export const buildPresentationVariants = (currentProduct, allProducts) => {
  const currentLabel = extractPresentationLabel(currentProduct.name);
  if (!currentLabel) return null;

  const familyKey = normalizeProductFamilyKey(currentProduct.name);
  const category = (currentProduct.category || '').trim().toLowerCase();

  const siblings = (allProducts || []).filter((p) => {
    if (p.status === 'inactive' || p.status === 'upcoming') return false;
    if ((p.category || '').trim().toLowerCase() !== category) return false;
    return normalizeProductFamilyKey(p.name) === familyKey;
  });

  if (siblings.length <= 1) return null;

  return siblings
    .map(mapProductToPresentationVariant)
    .sort((a, b) => parsePresentationValue(a.label) - parsePresentationValue(b.label));
};

const pickPrimaryVariant = (variants) =>
  variants.find((v) => v.stock > 0)
  || variants.find((v) => v.isDropshipping)
  || variants[0];

/** Una tarjeta por familia de producto en la tienda (ignora inventario vs dropshipping). */
export const groupProductsForStore = (products) => {
  const groupMap = new Map();
  const standalone = [];

  (products || []).forEach((product) => {
    const baseName = extractBaseName(product.name);
    const label = extractPresentationLabel(product.name);

    if (!label || !baseName) {
      standalone.push({ ...product, isGrouped: false, hasMultiplePresentations: false });
      return;
    }

    const key = `${(product.category || '').trim().toLowerCase()}|${normalizeProductFamilyKey(product.name)}`;
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key).push(product);
  });

  const grouped = [];

  groupMap.forEach((members) => {
    if (members.length === 1) {
      grouped.push({ ...members[0], isGrouped: false, hasMultiplePresentations: false });
      return;
    }

    const variants = members
      .map((p) => ({
        ...p,
        presentationLabel: extractPresentationLabel(p.name) || p.name,
      }))
      .sort((a, b) => parsePresentationValue(b.presentationLabel) - parsePresentationValue(a.presentationLabel));

    const primary = pickPrimaryVariant(variants);
    const prices = variants.map((v) => v.price).filter(Number.isFinite);
    const minPrice = prices.length ? Math.min(...prices) : primary.price;
    const maxPrice = prices.length ? Math.max(...prices) : primary.price;
    const base = members
      .map((m) => extractBaseName(m.name))
      .sort((a, b) => b.length - a.length)[0] || extractBaseName(primary.name);
    const claim =
      members.map((m) => extractProductClaim(m.name)).find(Boolean) || null;

    grouped.push({
      ...primary,
      id: primary.id,
      name: base,
      baseName: base,
      claim,
      isGrouped: true,
      hasMultiplePresentations: true,
      requiresPresentationSelect: true,
      presentationLabels: variants.map((v) => v.presentationLabel),
      minPrice,
      maxPrice,
      price: minPrice,
      inStock: variants.some((v) => v.inStock),
      isDropshipping: variants.every((v) => v.isDropshipping),
      variantIds: variants.map((v) => v.id),
    });
  });

  return [...grouped, ...standalone].map((p) => {
    if (p.isGrouped) return p;
    const baseName = extractBaseName(p.name);
    const claim = extractProductClaim(p.name);
    return {
      ...p,
      baseName,
      claim,
      name: baseName || p.name,
    };
  });
};

export const getPresentationPriceRange = (variants) => {
  if (!variants?.length) return null;
  const prices = variants.map((v) => v.price).filter(Number.isFinite);
  if (!prices.length) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
};

const DETAIL_MARKER = '---DETALLE---';
const SECTION_HEADER_RE = /^(beneficios clave|formas de uso|conservaci[oó]n|ingredientes|modo de uso|presentaci[oó]n|por qu[eé]|ideal para)/i;
const PRESENTATION_LINE_RE = /^presentaci[oó]n\s+\d/i;

/** Descripción más completa de la familia (misma para todas las presentaciones). */
export const pickFamilyDescription = (presentationVariants, fallbackDescription = '') => {
  const candidates = [
    fallbackDescription,
    ...(presentationVariants || []).map((v) => v.description),
  ].filter((t) => String(t || '').trim());
  if (!candidates.length) return '';
  return candidates.sort((a, b) => b.length - a.length)[0];
};

/**
 * Separa copy de ficha: resumen corto (hero) vs detalle (acordeón).
 * Convención BD opcional: párrafo corto + línea `---DETALLE---` + texto largo.
 */
export const splitProductDescription = (description) => {
  const raw = String(description || '').trim();
  if (!raw) return { summary: '', detail: '' };

  let summarySource = raw;
  let detailSource = raw;

  if (raw.includes(DETAIL_MARKER)) {
    const [head, ...tail] = raw.split(DETAIL_MARKER);
    summarySource = head.trim();
    detailSource = tail.join(DETAIL_MARKER).trim() || raw;
  } else {
    const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
    const summaryLines = [];
    for (const line of lines) {
      if (SECTION_HEADER_RE.test(line) || /^[•\-–]/.test(line)) break;
      if (PRESENTATION_LINE_RE.test(line)) continue;
      summaryLines.push(
        line.replace(/\s*presentaci[oó]n\s+\d+\s*ml\.?/gi, '').trim()
      );
    }
    summarySource = summaryLines.join(' ').replace(/\s{2,}/g, ' ').trim();
    detailSource = raw;
  }

  let summary = summarySource
    .replace(/\s*presentaci[oó]n\s+\d+\s*ml\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (summary.length > 280) {
    const cut = summary.slice(0, 277);
    const lastSpace = cut.lastIndexOf(' ');
    summary = `${(lastSpace > 180 ? cut.slice(0, lastSpace) : cut).trim()}…`;
  }

  return { summary, detail: detailSource };
};

/** Resuelve copy unificado para productos con o sin presentaciones múltiples. */
export const resolveProductPageCopy = (presentationVariants, description) => {
  const full = pickFamilyDescription(presentationVariants, description);
  return splitProductDescription(full);
};
